import { useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import { calculate } from "../../api/calculate";

export default function useCalculate(fundingAmount, startingSalary, options) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["calculate", { fundingAmount, startingSalary }], [fundingAmount, startingSalary]);
  
  const {
    data,
    refetch: refetchQuery,
    isFetching: isCalculating,
    isRefetching: isRecalculating,
  } = useQuery(queryKey, () => calculate(fundingAmount, startingSalary), {
    staleTime: 120 * 60 * 1000, // 2 hours - data is considered fresh
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours - keep in cache longer
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: false,
    ...options,
  });

  // Smart recalculate that respects cache
  const recalculate = useCallback(async () => {
    
    // Check if we already have fresh cached data for these exact parameters
    const cachedData = queryClient.getQueryData(queryKey);
    const queryState = queryClient.getQueryState(queryKey);
    
    if (cachedData && queryState?.dataUpdatedAt) {
      const timeSinceUpdate = Date.now() - queryState.dataUpdatedAt;
      const isStillFresh = timeSinceUpdate < 120 * 60 * 1000; // 2 hours
      
      if (isStillFresh) {
        // Data is still fresh, return cached data without making a request
        return { data: cachedData };
      }
    }
    
    // Data is stale or doesn't exist, make a fresh request
    return refetchQuery();
  }, [queryClient, queryKey, refetchQuery]);

  const formattedResult = useMemo(
    () => ({
      fundingData: data?.data,
      recalculate,
      isCalculating,
      isRecalculating,
    }),
    [data, recalculate, isCalculating, isRecalculating]
  );

  return formattedResult;
}
